<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { getRpmTypeLabel } from '@/util/fms-summary';

export default {
  name: 'RpmFuelSpeed',
  props: {
    series: {
      type: Array,
      required: true,
    },
    deviceStatus: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      resizeObserver: null,
      seriesVisibility: {},
    };
  },
  mounted() {
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
    deviceStatus: { deep: true, handler: 'drawChart' },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }
            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },

    processData() {
      if (!this.series) return null;

      const mapData = (path) => this.series.map((data) => {
        const keys = path.split('.');
        let val = data;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) {
          if (val && Object.prototype.hasOwnProperty.call(val, key)) {
            val = val[key];
          } else {
            val = null;
            break;
          }
        }
        if (val === null || val === undefined) return null;
        const num = Number(val);
        return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
      });

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));
      const datasets = [];

      // 1. Speed (always present)
      const speedData = mapData('gpsData.speed').map((val) => (val === null ? null : Number((val * 0.539957).toFixed(2))));
      datasets.push({
        name: 'Speed (kn)',
        data: speedData,
        interpolated: this.interpolateNulls(speedData),
        color: '#F59E0B',
        axis: 'speed',
      });

      // 2. RPM Data (based on isSingleMainEngine and deviceStatus)
      const rpmTypes = this.deviceStatus?.rpmTypes || [];
      const rpmColors = {
        port: '#8B5CF6', starboard: '#EC4899', main: '#8B5CF6', center: '#8B5CF6',
      };

      rpmTypes.forEach((type) => {
        const rpmData = mapData(`rpmData.${type.toUpperCase()}.rpm`);
        const displayLabel = getRpmTypeLabel(type, rpmTypes);
        datasets.push({
          name: `${displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1)} RPM`,
          data: rpmData,
          interpolated: this.interpolateNulls(rpmData),
          color: rpmColors[type.toLowerCase()] || '#000000',
          axis: 'rpm',
        });
      });

      // 3. Fuel Data (based on rpmTypes + flowmeterType)
      const flowmeterType = (this.deviceStatus?.flowmeterType || 'none').toLowerCase();
      const rpmTypesForFuel = (this.deviceStatus?.rpmTypes || [])
        .map((type) => String(type).toLowerCase());
      const fmPositions = rpmTypesForFuel.length > 0
        ? rpmTypesForFuel
        : (this.deviceStatus?.flowmeterPositions || []).map((pos) => String(pos).toLowerCase());
      const fuelColors = {
        port: '#3B82F6', starboard: '#06B6D4', center: '#3B82F6',
      };

      if (flowmeterType !== 'none') {
        fmPositions.forEach((pos) => {
          const position = String(pos).toLowerCase();
          if (position === 'port' || position === 'starboard' || position === 'center') {
            const fuelData = mapData(`fmData.${position.toUpperCase()}_FuelCons`);
            datasets.push({
              name: `${position.charAt(0).toUpperCase() + position.slice(1)} Fuel (L/h)`,
              data: fuelData,
              interpolated: this.interpolateNulls(fuelData),
              color: fuelColors[position] || '#000000',
              axis: 'fuel',
            });
          }
        });
      }

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const container = this.$refs.chartContainer;

      d3.select(container).selectAll('*').remove();

      const visibility = this.seriesVisibility || {};
      datasets.forEach((dataset) => {
        if (visibility[dataset.name] === undefined) visibility[dataset.name] = true;
      });
      this.seriesVisibility = visibility;

      // Margin kanan lebih besar untuk menampung 2 sumbu
      const margin = {
        top: 20, right: 120, bottom: 50, left: 60,
      };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 600 - margin.top - margin.bottom;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // X Axis
      const x = d3.scaleTime().domain(d3.extent(timestamps)).range([0, width]);
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat((d) => formatDateTime(d, 'DD/MM HH:mm')))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // -- Scales Setup --

      // 1. Fuel (Left Axis) - Main
      const maxFuel = d3.max(datasets.filter((d) => d.axis === 'fuel').flatMap((d) => d.interpolated)) || 10;
      const yFuel = d3.scaleLinear().domain([0, maxFuel * 1.1]).range([height, 0]);

      svg.append('g').call(d3.axisLeft(yFuel))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', -40)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Fuel (L/h)');

      // 2. RPM (Right Axis 1)
      const maxRpm = d3.max(datasets.filter((d) => d.axis === 'rpm').flatMap((d) => d.interpolated)) || 1000;
      const yRpm = d3.scaleLinear().domain([0, (Math.ceil(maxRpm / 100) * 100) + 100]).range([height, 0]);

      svg.append('g').attr('transform', `translate(${width}, 0)`).call(d3.axisRight(yRpm))
        .append('text')
        .attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('RPM');

      // 3. Speed (Right Axis 2 - Offset)
      const maxSpeed = d3.max(datasets.filter((d) => d.axis === 'speed').flatMap((d) => d.interpolated)) || 20;
      const ySpeed = d3.scaleLinear().domain([0, maxSpeed * 1.1]).range([height, 0]);

      svg.append('g').attr('transform', `translate(${width + 60}, 0)`).call(d3.axisRight(ySpeed))
        .append('text')
        .attr('fill', '#F59E0B')
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Speed (kn)');

      // Grid (Based on Fuel Axis)
      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yFuel).tickSize(-width).tickFormat(''));

      // Helper to select scale
      const getScale = (axisName) => {
        if (axisName === 'fuel') return yFuel;
        if (axisName === 'rpm') return yRpm;
        return ySpeed;
      };

      // Draw Lines & Dots
      const seriesElements = {};
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      datasets.forEach((series, index) => {
        const yScale = getScale(series.axis);

        // Line Interpolated (Dashed)
        const interpolatedLine = svg.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.6)
          .attr('d', d3.line().x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real (Solid)
        const realLine = svg.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots for Real Data
        const dots = svg.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');

        seriesElements[series.name] = {
          interpolatedLine,
          realLine,
          dots,
        };
      });

      // Tooltip & Hover Interaction
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');

      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);

      const focusCircles = datasets.map((ds) => focusGroup.append('circle').attr('r', 5).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      const updateSeriesVisibility = () => {
        datasets.forEach((ds, index) => {
          const isVisible = visibility[ds.name];
          const elements = seriesElements[ds.name];
          elements.interpolatedLine.style('display', isVisible ? null : 'none');
          elements.realLine.style('display', isVisible ? null : 'none');
          elements.dots.style('display', isVisible ? null : 'none');
          focusCircles[index].style('display', isVisible ? null : 'none');
        });
      };

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -10)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          visibility[d.name] = !visibility[d.name];
          updateSeriesVisibility();
          // eslint-disable-next-line no-use-before-define
          updateLegend();
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      const updateLegend = () => {
        legendItem.select('rect').attr('opacity', (d) => (visibility[d.name] ? 1 : 0.3));
        legendItem.select('text')
          .attr('opacity', (d) => (visibility[d.name] ? 1 : 0.4))
          .style('text-decoration', (d) => (visibility[d.name] ? 'none' : 'line-through'));
      };

      updateSeriesVisibility();
      updateLegend();

      svg.append('rect')
        .attr('width', width).attr('height', height).style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);

          datasets.forEach((ds, i) => {
            if (!visibility[ds.name]) {
              focusCircles[i].style('display', 'none');
              return;
            }
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[i].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[i].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, 'DD MMM YYYY HH:mm')}</strong><br/>`;
          datasets.forEach((ds) => {
            if (!visibility[ds.name]) return;
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px;">
                          <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                          ${ds.name}: ${val}
                        </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          // Prevent tooltip going off-screen on the right
          if (left + 160 > width) left = posX - 170;

          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: Helvetica, Arial, sans-serif; background: #fff; }
.chart-container { width: 100%; }
</style>
